import { ConfirmationModalPayload } from 'src/app/shared/components/modals/confirmation-modal/axle-confirmation-modal.component';
import { moveArrayItem } from 'src/app/shared/functions/array.functions';
import {
  AddMarketedOrg,
  MarketedOrg
} from 'src/app/shared/services/events/events.models';
import {
  CorpRegConfirmationModalType,
  CorpRegEntityType,
  confirmationText,
  scheduleModeRemoveHostsSubheader
} from 'src/app/shared/services/events/host-management/event-host-management.constants';
import { ModalService } from 'src/app/shared/services/modal/modal.service';

interface HostChangeConfirmationContext {
  type: CorpRegConfirmationModalType;
  entityType: CorpRegEntityType;
  presentationsEnabled?: boolean;
  /** Schedule mode has no activities, so its copy only mentions meetings. */
  scheduleMode?: boolean;
}

interface OpenHostChangeConfirmationModalOptions extends HostChangeConfirmationContext {
  onConfirm: () => void;
  /** Called when the modal closes without confirming. */
  onCancel?: () => void;
}

// Moves a user within one org. Returns null when the org is missing or the
// move is a no-op. Never mutates the input.
export function moveMarketedUser(
  orgs: readonly MarketedOrg[],
  orgId: string,
  fromIndex: number,
  toIndex: number
): MarketedOrg[] | null {
  const org = orgs.find((item) => item.orgId === orgId);
  if (!org) return null;
  const users = moveArrayItem(org.users, fromIndex, toIndex);
  if (!users) return null;
  return orgs.map((item) => (item.orgId === orgId ? { ...item, users } : item));
}

// Array position is the rank: the backend stores each `index` as displayRank
// and falls back to 1000 when it's missing, so every org and user needs one.
export function buildHostRankPayload(
  orgs: readonly MarketedOrg[]
): AddMarketedOrg[] {
  return orgs.map(({ orgId, takingMeetings, presenting, users }, index) => ({
    orgId,
    index,
    takingMeetings,
    presenting,
    users: users.map(({ id, takingMeetings, presenting }, userIndex) => ({
      userId: id,
      index: userIndex,
      takingMeetings,
      presenting
    }))
  }));
}

export function openHostChangeConfirmationModal(
  modalService: ModalService,
  options: OpenHostChangeConfirmationModalOptions
): void {
  let confirmed = false;

  modalService.openModal<ConfirmationModalPayload, undefined>(
    'confirmation',
    {
      header: confirmationText[options.type],
      subheader: getHostChangeConfirmationSubheader(options),
      type: 'destructive',
      secondaryButtonText: 'Cancel',
      buttonText: confirmationText[options.type],
      confirmClicked: () => {
        confirmed = true;
        options.onConfirm();
        modalService.close('confirmation');
      }
    },
    () => {
      if (!confirmed) options.onCancel?.();
    }
  );
}

function getHostChangeConfirmationSubheader(
  options: HostChangeConfirmationContext
): string {
  const { type, entityType, presentationsEnabled, scheduleMode } = options;

  if (entityType === 'BULK') {
    if (scheduleMode) return scheduleModeRemoveHostsSubheader;
    return 'This will cancel any meetings or activities where there are no longer hosts. Continue?';
  }

  if (entityType === 'USER') {
    if (type === 'REMOVE_FROM_EVENT' && !scheduleMode) {
      return 'Proceeding will cancel any scheduled meetings or activities for this host. Continue?';
    }
    return 'Proceeding will cancel any scheduled meetings for this host. Continue?';
  }

  if (type === 'REMOVE_FROM_EVENT') {
    const meetingsSuffix = presentationsEnabled
      ? ', activities, and presentations.'
      : ' and activities.';
    return `This will remove all hosts from the event and cancel scheduled meetings${meetingsSuffix} Continue?`;
  }
  if (type === 'REMOVE_MEETINGS') {
    return 'This will mark all meeting-only hosts as Declined and remove their scheduled meetings.';
  }
  if (type === 'REMOVE_PRESENTATIONS') {
    return 'This will mark all presentation-only hosts as Declined and remove their scheduled presentation.';
  }

  return '';
}
